import CitySEOHead from "@/components/feature/CitySEOHead";
import OgdenHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";

const ogdenProcessingOptions = [
  {
    speed: "24",
    unit: "Hours*",
    label: "SLC Passport Agency",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Appointment required — no walk-ins accepted",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis",
      "35 miles south — full-day round trip",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Predictable turnaround — no guessing",
      "Ideal for Hill AFB PCS orders, Weber State study abroad",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service",
      "Good for planned travel — family vacations, cruises, reunions",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "Ogden, Layton & Clearfield post offices book weeks out",
      "In-person clerk appointment required",
      "No document review — errors cause rejection",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Can't upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export const faqs = [
  {
    q: "Where is the nearest passport agency to Ogden?",
    a: "The nearest regional agency is the Salt Lake City Passport Agency at 324 S State St, Suite 225 — about 35 miles south of Ogden. Walk-in appointments are rarely available. Our courier service bypasses the agency line entirely.",
  },
  {
    q: "How fast can I get a passport in Ogden, Utah?",
    a: "Through our expedited courier service, passports can be processed as fast as 24 hours for true emergencies. Rush service takes 3–5 business days. Standard expedited takes 5–7 days. We specialize in military and family passport needs.",
  },
  {
    q: "Do I need to visit an office for Ogden passport service?",
    a: "No office visit required for our service. You mail us your documents with our prepaid FedEx label. For first-time applicants and minors, you'll need to visit an acceptance facility before we take over.",
  },
  {
    q: "I'm a Hill AFB family with PCS orders to Germany — can you help?",
    a: "Absolutely. We regularly process military family passports for Hill AFB personnel. We understand the urgency of PCS orders and our emergency service is available for last-minute deployment scenarios.",
  },
  {
    q: "Can I get a passport for my family of five in Ogden?",
    a: "Yes. We process family applications together to minimize turnaround time. Each member needs their own application, but we handle them simultaneously. This is especially common for military families and family reunion travel.",
  },
  {
    q: "What post offices in Ogden accept passport applications?",
    a: "The Ogden Main Post Office at 2259 Washington Blvd accepts passport applications. Other nearby options include Layton Post Office (846 N Main St, Layton) and Clearfield Post Office (69 N Main St, Clearfield). Appointments are recommended.",
  },
  {
    q: "How do I avoid common mistakes in Ogden passport applications?",
    a: "The most common mistakes are: waiting too late for post office appointments, wrong photo size, and incomplete parental consent for minors. We review your documents before submission and provide a complete checklist.",
  },
  {
    q: "Is your service more affordable than going through the SLC agency?",
    a: "For most travelers, yes. Our courier service is typically 30–100% less than FedEx and other resellers. The agency requires a full-day visit (lost wages) and rarely accepts walk-ins. We handle the entire process by mail.",
  },
];

export default function OgdenPage() {
  return (
    <>
      <CitySEOHead
        city="Ogden"
        state="Utah"
        stateAbbr="UT"
        route="/ogden"
        title="Emergency Passport Ogden, UT — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in Ogden, UT — as fast as 24 hours. Serving Hill AFB military families, Weber State University students, Davis County residents, and Northern Utah families. Registered State Dept. courier. A+ BBB rated."
        geoPlacename="Ogden, Utah"
        geoPosition="41.2230;-111.9738"
        faqs={faqs}
        servicesSchema={[
          { name: "Emergency Passport Ogden UT", description: "Urgent passport processing for Ogden Utah travelers including Hill AFB military families with immediate departure needs." },
          { name: "Expedited Passport Renewal Ogden UT", description: "Fast passport renewal for Ogden Utah residents with a previously-issued passport." },
          { name: "First-Time Passport Ogden UT", description: "New passport application services for Ogden Utah and Weber County residents applying for their first passport." },
          { name: "Child Passport Ogden UT", description: "Minor passport services for Ogden Utah families with children under 16." },
          { name: "Name Change Passport Ogden UT", description: "Passport name correction after marriage, divorce, or legal name changes for Ogden residents." },
          { name: "Lost Stolen Passport Ogden UT", description: "Replacement passport service for Ogden residents with lost or stolen travel documents, including DS-64 guidance." },
          { name: "Second Passport Ogden UT", description: "Second passport services for Ogden Utah frequent international travelers and Hill AFB personnel." },
        ]}
        keywords="emergency passport Ogden, expedited passport Ogden Utah, Hill AFB passport, Weber State passport, passport renewal Ogden"
        statePageName="Utah"
        statePageRoute="/states/utah"
      />
      <div className="font-sans">
        <OgdenHeroSection />

        <div className="bg-[#1A365D] text-white sticky top-0 z-40 hidden lg:block">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center gap-8 h-12 overflow-x-auto">
              {[
                { label: "Processing", href: "#processing" },
                { label: "Services", href: "#services" },
                { label: "Local Area", href: "#local-area" },
                { label: "Travel Timing", href: "#travel-timing" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "Facilities", href: "#facilities" },
                { label: "Compare", href: "#compare" },
                { label: "How It Works", href: "#how-it-works" },
                { label: "FAQ", href: "#faq" },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm text-white/80 hover:text-[#F6C344] whitespace-nowrap transition-colors py-3 border-b-2 border-transparent hover:border-[#F6C344]">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <SharedProcessingTimeSection
          sectionId="processing"
          cityName="Ogden"
          headingExtra="UT"
          introText="The SLC Passport Agency is 35 miles south on I-15 and appointments book out weeks in advance — walk-ins are only for verified life-or-death emergencies. Our registered courier service hand-delivers your application daily, so Hill AFB military families, Weber State students, Davis County residents, and Northern Utah families get their passports on a predictable timeline — no appointment lottery, no waiting room, no drive to Salt Lake."
          options={ogdenProcessingOptions}
          emergencyDescription="The SLC Passport Agency at 324 S State St, 35 miles south of Ogden, serves verified life-or-death emergencies only. You must call NPIC (877-487-2778) and have proof of imminent international travel within 72 hours. Appointments are extremely limited and not guaranteed — and you're competing with the entire Wasatch Front for a slot."
          disclaimerText="* Emergency processing availability depends on U.S. Department of State volume, appointment slots, and your specific circumstances. Travel must be within 72 hours for agency emergency appointments. Courier processing times are based on business days and are not guaranteed — but our daily submission schedule gives Ogden and Northern Utah residents the fastest legally available route."
          afterCardsContent={
            <div className="mt-16">
              <div className="text-center mb-10">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-4">Where Ogden Travelers Are Going</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  { city: "Tokyo, Japan", flag: "🇯🇵", note: "Hill AFB deployment & tech" },
                  { city: "London, UK", flag: "🇬🇧", note: "Military PCS & business" },
                  { city: "Seoul, South Korea", flag: "🇰🇷", note: "Military partnership" },
                  { city: "Frankfurt, Germany", flag: "🇩🇪", note: "Hill AFB military PCS" },
                  { city: "Cancun, Mexico", flag: "🇲🇽", note: "Family vacation & cruise" },
                  { city: "Paris, France", flag: "🇫🇷", note: "Weber State exchange" },
                ].map((dest) => (
                  <div key={dest.city} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg p-4">
                    <span className="text-2xl">{dest.flag}</span>
                    <div>
                      <p className="font-bold text-[#1A365D] text-sm">{dest.city}</p>
                      <p className="text-gray-500 text-xs">{dest.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { stat: "5,000+", label: "Hill AFB active duty" },
                  { stat: "24 hrs", label: "Fastest turnaround" },
                  { stat: "20+ yrs", label: "Serving Utah families" },
                  { stat: "4.9★", label: "Customer rating" },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                    <p className="font-bold text-[#1A365D] text-2xl mb-1">{s.stat}</p>
                    <p className="text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Handle</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Ogden &amp; Northern Utah</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-passport-line", title: "Passport Renewal", desc: "Adult renewals by mail with no post office visit. Fastest route to a new 10-year passport for previously issued adult passports." },
                { icon: "ri-user-add-line", title: "First-Time Passport", desc: "Complete DS-11 guidance and acceptance facility support for first-time applicants throughout Davis County and Weber County." },
                { icon: "ri-parent-line", title: "Child Passport", desc: "Minor under 16? Both parents must consent. We guide you through the DS-11 minor requirements and acceptance clerk appointment." },
                { icon: "ri-alert-line", title: "Lost or Stolen Passport", desc: "DS-64 filing plus replacement application. We handle the paperwork and the courier leg together." },
                { icon: "ri-file-edit-line", title: "Name Change Passport", desc: "Post-marriage or legal name change? We process DS-5504 corrections for recently issued passports and full renewals for older ones." },
                { icon: "ri-global-line", title: "Second Passport", desc: "Required for frequent travelers to countries with conflicting visa stamp policies. We handle this specialized request regularly." },
              ].map((svc) => (
                <div key={svc.title} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${svc.icon} text-[#1A365D] text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-lg mb-2">{svc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="local-area" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Ogden Context</p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">Why Ogden Residents Need Expedited Passport Help</h2>
                <div className="text-gray-600 text-sm leading-relaxed space-y-4">
                  <p>Ogden occupies a strategic position in Northern Utah, serving as the economic and military anchor of the Wasatch Front's northern corridor. Home to Hill Air Force Base — one of the largest military installations in the intermountain West with over 5,000 active-duty personnel and 13,000 civilian employees — Ogden generates a consistent stream of urgent passport needs that are far beyond what most cities of 87,000 residents would experience. Military families facing short-notice PCS (Permanent Change of Station) orders to Germany, Japan, or the UK often find themselves with only 10-14 days to produce valid passports for the entire family. When the Salt Lake City regional passport agency is 35 miles away and routinely requires 2-week advance appointments, the logistics become impossible for a family already managing a cross-continental relocation.</p>
                  <p>Weber State University adds another layer of demand. Its 30,000+ students participate in international programs, nursing clinical rotations abroad, and engineering partnerships with overseas institutions. The historic 25th Street district, with its growing restaurant and tourism scene, also sees service industry workers and small business owners traveling internationally for trade shows and culinary exchanges. The Ogden-Hinckley Airport supports commercial flights to several western cities, creating a natural travel hub for Northern Utah residents who want to avoid the Salt Lake City commute.</p>
                  <p>The geographic challenge for Ogden residents is clear: the Salt Lake City regional passport agency requires a full-day trip, and local post offices in Davis and Weber counties book appointment slots weeks out. Our courier service eliminates the commute entirely. You send us your documents from Ogden, Layton, or Roy — and we hand-deliver to the State Department in Washington, D.C., returning your completed passport via FedEx overnight.</p>
                </div>
              </div>
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Hill%20Air%20Force%20Base%20in%20Ogden%20Utah%20with%20the%20Wasatch%20Mountains%20in%20the%20background%2C%20military%20aircraft%20and%20facilities%20visible%20on%20the%20expansive%20base%2C%20sprawling%20infrastructure%2C%20dramatic%20mountain%20landscape%2C%20clear%20sky%2C%20professional%20aerial%20photography%2C%20no%20readable%20text%20or%20signs&width=700&height=450&seq=ogden-hafb&orientation=landscape"
                  alt="Hill Air Force Base in Ogden Utah"
                  className="w-full h-[250px] object-cover rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Nearest Passport Agency", value: "SLC — 35 miles south" },
                    { label: "Hill AFB Personnel", value: "5,000+ active duty" },
                    { label: "Weber State Enrollment", value: "30,000+ students" },
                    { label: "Key Industries", value: "Defense, Aerospace, Education" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#F7F8FA] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="font-bold text-[#1A365D] text-sm">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="travel-timing" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Ogden Passport Timing Guide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { scenario: "Hill AFB PCS Orders", window: "Apply within 48 hours of orders", urgency: "High", icon: "ri-shield-star-line", detail: "PCS orders often arrive with 10-14 days notice. We handle military family passports as a priority with our emergency service." },
                { scenario: "Weber State Study Abroad", window: "Apply 3-4 weeks before departure", urgency: "Medium", icon: "ri-graduation-cap-line", detail: "Weber State's international programs cover nursing, engineering, and business. Apply immediately upon acceptance." },
                { scenario: "Family Vacation or Cruise", window: "Apply 6-8 weeks in advance", urgency: "Low-Medium", icon: "ri-plane-line", detail: "Summer cruises and European trips often book months out. Standard expedited is sufficient, but don't wait past 6 weeks." },
                { scenario: "Business or Conference Travel", window: "Apply as soon as trip is confirmed", urgency: "Variable", icon: "ri-briefcase-line", detail: "Northern Utah's aerospace and defense contractors often travel internationally. Renew or apply as soon as your flight is confirmed." },
              ].map((item) => (
                <div key={item.scenario} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${item.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{item.scenario}</h3>
                  <p className="text-[#F6C344] text-xs font-bold mb-2 uppercase">{item.window}</p>
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${item.urgency === "High" ? "bg-red-100 text-red-700" : item.urgency === "Medium" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{item.urgency} Urgency</span>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>



        <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Real Stories</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Ogden &amp; Davis County Clients</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Jennifer K.", location: "Layton, UT", text: "My husband got PCS orders to Germany with 10 days notice. We needed 4 passports. They had all 4 done in 6 days. I have no idea how we would have managed without them.", stars: 5 },
                { name: "Marcus T.", location: "Ogden, UT", text: "Weber State study abroad to Florence. Passport expired 3 weeks before. They got me through in 5 days. Amazing service.", stars: 5 },
                { name: "Diana R.", location: "Roy, UT", text: "Family cruise with the grandkids. Three kids needed first-time passports. Everything was done in 8 days, and they walked us through the whole process.", stars: 5 },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="facilities" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Ogden</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm">First-time applicants and minors must visit an acceptance facility before we begin the courier process.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "Ogden Main Post Office", address: "2259 Washington Blvd, Ogden", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Layton Post Office", address: "846 N Main St, Layton", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Clearfield Post Office", address: "69 N Main St, Clearfield", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Bountiful Post Office", address: "20 E 500 S, Bountiful", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Roy Post Office", address: "5025 S 1900 W, Roy", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "SLC Passport Agency", address: "324 S State St, Suite 225, SLC", hours: "Mon-Fri 8am-3pm (by appt)", type: "Regional Agency (35 mi)" },
              ].map((fac) => (
                <div key={fac.name} className="border border-gray-200 rounded-lg p-6">
                  <span className="inline-block bg-[#EEF2F8] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{fac.type}</span>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{fac.name}</h3>
                  <p className="text-gray-600 text-sm mb-1"><i className="ri-map-pin-line mr-1.5"></i>{fac.address}</p>
                  <p className="text-gray-500 text-xs"><i className="ri-time-line mr-1.5"></i>{fac.hours}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="compare" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Alternatives</h2>
            </div>

            <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-5 md:p-6 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-base text-[#1A365D] mb-1">Passport Agency Appointment Not Available?</h3>
                  <p className="text-sm text-gray-600">Don't let a booked-out agency appointment block your travel plans. We bypass the agency bottleneck with reserved daily courier slots — faster and no appointment roulette.</p>
                </div>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A365D] text-white">
                    <th className="text-left px-4 py-3 font-semibold rounded-tl-lg">Feature</th>
                    <th className="text-center px-4 py-3 font-semibold text-[#F6C344]">Fast Passport Center</th>
                    <th className="text-center px-4 py-3 font-semibold">SLC Agency (35 mi)</th>
                    <th className="text-center px-4 py-3 font-semibold rounded-tr-lg">FedEx / UPS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["No office visit required", "✓", "✗", "✗"],
                    ["As fast as 24 hours", "✓", "Same-day only", "✗"],
                    ["Price vs. resellers", "30-100% less", "Govt fee only", "Full markup"],
                    ["Registered State Dept. courier", "✓", "N/A", "✗"],
                    ["Military family specialist", "✓", "Limited", "✗"],
                    ["Document error review", "✓", "✗", "✗"],
                    ["Handles family applications", "✓", "✗", "✗"],
                    ["FedEx delivery to your door", "✓", "Pick up in person", "Partial"],
                    ["A+ BBB rated", "✓", "N/A", "Varies"],
                  ].map(([feat, us, agency, fedex], i) => (
                    <tr key={feat} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{feat}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{us}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{agency}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{fedex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Ogden Passport Mistakes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Waiting for post office appointment slots", desc: "Ogden, Layton, and Clearfield post offices book 2-4 weeks out in spring. Apply online through us immediately." },
                { title: "Assuming Hill AFB has passport processing", desc: "The base provides PCS guidance but doesn't process passports. You still need to apply through standard channels." },
                { title: "Not realizing the SLC agency requires 14-day proof", desc: "The agency only accepts walk-ins with proof of departure within 14 days. Our courier works with any timeline." },
                { title: "Wrong photo background or glasses", desc: "White background required, no glasses allowed. We review all submitted photos before courier dispatch." },
                { title: "Missing minor consent requirements", desc: "Both parents must appear for children under 16. We provide a complete checklist so nothing is missed." },
                { title: "Not applying for the whole family at once", desc: "Military families need 3-5 passports simultaneously. Our process handles all family members together." },
              ].map((m) => (
                <div key={m.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-600 text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Ogden Residents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Order Online", desc: "Select your service and complete our secure order. 5 minutes, no paperwork needed to start." },
                { step: "2", title: "Mail Documents", desc: "Use our prepaid FedEx label from any drop location in Ogden, Layton, or Roy." },
                { step: "3", title: "We Courier to D.C.", desc: "Our registered courier hand-delivers your application to the State Department in Washington, D.C." },
                { step: "4", title: "Delivered to Your Door", desc: "Your completed passport ships via FedEx Priority Overnight to your Ogden area address." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-xl mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Ogden Passport FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="border border-gray-200 rounded-lg group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</h4>
                    <i className="ri-add-line text-[#1A365D] flex-shrink-0 group-open:hidden"></i>
                    <i className="ri-subtract-line text-[#1A365D] flex-shrink-0 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Official Links</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Ogden Passport Applicants</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "U.S. State Dept. Passport Info", url: "https://travel.state.gov", icon: "ri-government-line" },
                { title: "SLC Passport Agency (35 mi south)", url: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html", icon: "ri-building-line" },
                { title: "Hill AFB Travel Information", url: "https://www.hill.af.mil", icon: "ri-shield-star-line" },
                { title: "DS-82 Renewal Form", url: "https://travel.state.gov/content/travel/en/passports/need-passport/ds-82.html", icon: "ri-file-text-line" },
                { title: "Weber State University", url: "https://www.weber.edu", icon: "ri-graduation-cap-line" },
                { title: "Utah DMV (ID Documentation)", url: "https://dld.utah.gov", icon: "ri-id-card-line" },
              ].map((r) => (
                <a key={r.title} href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-4 bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <span className="text-[#1A365D] font-medium text-sm group-hover:text-[#F6C344] transition-colors">{r.title}</span>
                  <i className="ri-external-link-line text-gray-400 ml-auto"></i>
                </a>
              ))}
            </div>
          </div>
        </section>

        <SEOCrossLinks currentRoute="/cities/ogden" state="UT" />

        <section className="bg-[#1A365D] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get Started Today</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">Ogden's Fastest Passport Service — Military &amp; Family Specialists</h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">PCS orders, study abroad, or a family vacation — we handle it all. As fast as 24 hours, delivered to your door in Ogden, Layton, or Roy.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>
              <a href="tel:8772530084" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                <i className="ri-phone-line"></i>877-253-0084
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}